import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { SecurityModuleService } from '@core/services';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import { Package, PackageCreate, PackageUpdate, SurveillanceLog, SurveillanceLogCreate, SurveillanceLogUpdate, Visit, VisitCreate, VisitUpdate } from '@core/types/security-module.type';

@Injectable({ providedIn: 'root' })
export class SecurityModuleQuery {
  private readonly service = inject(SecurityModuleService);
  private readonly queryClient = inject(QueryClient);

  // ===== Visits =====
  useVisitsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.visits.grid(c),
        queryFn: () => lastValueFrom(this.service.visitsGrid(c)),
        select: (res: ApiResponse<PaginationResult<Visit>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateVisit() {
    return injectMutation(() => ({
      mutationFn: (data: VisitCreate) => lastValueFrom(this.service.createVisit(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.visits.all }),
    }));
  }

  useUpdateVisit() {
    return injectMutation(() => ({
      mutationFn: (data: VisitUpdate) => lastValueFrom(this.service.updateVisit(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.visits.all }),
    }));
  }

  useDeleteVisit() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteVisit(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.visits.all }),
    }));
  }

  // ===== Packages =====
  usePackagesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.packages.grid(c),
        queryFn: () => lastValueFrom(this.service.packagesGrid(c)),
        select: (res: ApiResponse<PaginationResult<Package>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreatePackage() {
    return injectMutation(() => ({
      mutationFn: (data: PackageCreate) => lastValueFrom(this.service.createPackage(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.packages.all }),
    }));
  }

  useUpdatePackage() {
    return injectMutation(() => ({
      mutationFn: (data: PackageUpdate) => lastValueFrom(this.service.updatePackage(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.packages.all }),
    }));
  }

  useDeletePackage() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deletePackage(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.packages.all }),
    }));
  }

  // ===== Surveillance Logs =====
  useSurveillanceLogsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.surveillanceLogs.grid(c),
        queryFn: () => lastValueFrom(this.service.surveillanceLogsGrid(c)),
        select: (res: ApiResponse<PaginationResult<SurveillanceLog>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateSurveillanceLog() {
    return injectMutation(() => ({
      mutationFn: (data: SurveillanceLogCreate) => lastValueFrom(this.service.createSurveillanceLog(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.surveillanceLogs.all }),
    }));
  }

  useUpdateSurveillanceLog() {
    return injectMutation(() => ({
      mutationFn: (data: SurveillanceLogUpdate) => lastValueFrom(this.service.updateSurveillanceLog(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.surveillanceLogs.all }),
    }));
  }

  useDeleteSurveillanceLog() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteSurveillanceLog(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.surveillanceLogs.all }),
    }));
  }
}

